/**
 *  @param 用户管理模块
 *  @description 实现用户状态，用户缓存持久化功能
 */
import { defineStore, storeToRefs } from "pinia"
import { container, instanceCachingFactory, registry, singleton } from "tsyringe"
import type { App, Ref } from "vue"
import { CORE_INITIALIZER, CORE_USER, CORE_AXIOS } from "../CoreSymbol"
import type { Http } from './AxiosInitializer'
import type { Initializer } from "./Initializer"
import { isObject } from "lodash"

/**
 * @title State类型
 * @description 暂时用此方法作对象输出处理，waiting Update
 */
export type User = {
  [x: string]: any
}

export type UserState = {
  user: User
}

const createDefaultUserState = (): UserState => {
  return {
    user: {},
  } as UserState
}

const defaultUserState = createDefaultUserState()

/**
 * @title 用户管理模块
 * @description 操作用户相关数据
 */
export interface UserService {
  /**
   * @description 获取用户数据信息
   */
  getUser(): Ref<User>
  /**
   * @description 修改数据方法
   * @param {object} Object 
   * @returns {boolean}  boolean [false-失败 | true-成功]
   */
  setUser<K>(k: K): boolean
  /**
   * 清理用户
   */
  reset(): void
  /**
   * @description 判断有无用户缓存信息
   * @returns {boolean} boolean [false-没有用户缓存 | true-有用户缓存]
   */
  isUserValid(): boolean
}

/**
 * @title 生成Store
 */
export const userStore = defineStore(CORE_USER.toString(), {
  state: () => defaultUserState,
  // 开启持久化
  persist: {
    enabled: true, // 启用
    strategies: [{
      key: 'user',
      // storage 可选localStorage或sessionStorage
      storage: localStorage
      // paths 给指定数据持久化
    }]
  }
})

/**
 *  @title Service 实现层
 */
export class DefaultUserService implements UserService {
  store: any
  constructor() {
    this.store = userStore()
  }

  /**
   * @description 获取用户信息
   */
  getUser(): Ref<User> {
    const { user } = storeToRefs(this.store)
    return user
  }

  /**
   *  @description 数据插入方法
   */
  setUser<K>(data: K): boolean {
    try {
      if (data.user.id) {
        const axios = container.resolve<Http>(CORE_AXIOS)
        axios.get(`/v1/api/user/sale/${data.user.id}`).then(res => {
          this.store.$patch({ user: res })
        })
      }
      this.store.$patch(data)
      return true
    } catch {
      return false
    }
  }

  reset(): void {
    this.store.$state = createDefaultUserState()
  }

  /**
   * @description 判断有无用户缓存信息
   * @returns {boolean} boolean [false : 没有用户缓存 | true : 有用户缓存]
   */
  isUserValid(): boolean {
    const user = this.store.user
    return user && (user.email || user.username || user.mobile) && user.type
  }
}

/**
 *  Register 注册
 */
@singleton()
@registry([{
  token: CORE_INITIALIZER,
  useFactory: instanceCachingFactory<Initializer>(c => c.resolve(UserInitService))
}])
export class UserInitService implements Initializer {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  start(app: App<Element>): void {
    container.register<UserService>(CORE_USER, { useValue: new DefaultUserService() })
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  started(app: App<Element>): void {

  }
}
